// Blood Moon — custom timed event (custom-events-v1 §E2). For 12 s the
// swarm frenzies: every alive and newly spawned enemy moves at +35% speed.
//
// Mechanics (spec):
// - Frenzy speed: +35% for all enemies, applied as a displacement
//   post-adjust (see post-adjust.ts — the ion-storm pattern; scaling vel
//   would be fought by enemy steering). Newly spawned enemies are covered
//   automatically: the tracker rescales whatever enemy-ai integrated.
// - Contact damage +25%: DEFERRED. Contact damage is computed in
//   enemy-ai.ts (forbidden file) from the def's contact_damage with no
//   world-scoped multiplier hook anywhere in the damage path — per the spec,
//   without an existing hook the frenzy is speed-only.
// - Reward (xp motes ×2, crate chance ×2): the event writes the world flag
//   `world.eventFlags.bloodMoon` in start()/clears in end(); the READ side
//   in kill.ts is deferred to the integration pass (kill.ts is owned by
//   another agent right now). The flag is also cleared by world.reset().
//
// Determinism (GDD Appendix A §A.1): pure function of world state and tick —
// no RNG at all, no Math.random, no wall-clock. Pool iteration is in
// pool_index order.
//
// Perf: one pass over the enemy pool per tick; tracker entries are reused,
// so steady-state update() allocates nothing.
//
// Visuals (wired by the parent, not here): 'phase_change'
//   { system: 'events', event_id: 'blood_moon', phase: 'start'|'end', danger: 3, duration }
// Suggested look: red tint pass over the whole scene, enemies pulse.

import type { WorldState } from '../state.js';
import type { GameEventDef } from '../events-manager.js';
import { DisplacementScaler } from './post-adjust.js';

/** Event lifetime: 12 s at 60 Hz. */
const EVENT_DURATION_TICKS = 12 * 60;
/** Frenzy speed multiplier applied to every enemy's displacement. */
const FRENZY_FACTOR = 1.35;

/**
 * Create the Blood Moon event def. All mutable state lives in this closure
 * and is fully reset in start()/end().
 */
export function createBloodMoonEvent(): GameEventDef {
  const frenzy = new DisplacementScaler();

  return {
    id: 'blood_moon',
    name: 'Blood Moon',
    duration: EVENT_DURATION_TICKS,

    start(world: WorldState, tick: number): void {
      frenzy.reset();
      // Drop-doubling flag (WRITE side). Fresh object each activation; the
      // kill path reads `world.eventFlags?.bloodMoon` with a ?? fallback.
      world.eventFlags = { bloodMoon: true };
      world.events.emit(
        'phase_change',
        { system: 'events', event_id: 'blood_moon', phase: 'start', danger: 3, duration: EVENT_DURATION_TICKS },
        tick
      );
    },

    update(world: WorldState, _tick: number, _progress: number): void {
      frenzy.apply(world.enemies.alive(), FRENZY_FACTOR);
    },

    end(world: WorldState, tick: number): void {
      frenzy.reset();
      world.eventFlags = undefined;
      world.events.emit(
        'phase_change',
        { system: 'events', event_id: 'blood_moon', phase: 'end', danger: 3 },
        tick
      );
    }
  };
}
